import {Injectable} from '@angular/core';
import {EventService, RoutingService, SearchboxService, TranslationService, WindowRef} from '@spartacus/core';
import {SearchBoxComponentService} from '@spartacus/storefront';
import {combineLatest, Observable, of} from 'rxjs';
import {filter, map, tap} from 'rxjs/operators';

import {AimoSearchConfig} from '../../../model/misc.model';
import {
    AimoProductSearchPage,
    AimoSearchBoxConfig,
    AimoSearchResults,
    AimoSuggestion
} from '../../../model/product.model';

import {AimoProductSearchService} from './aimo-product-search.service';

const HAS_SEARCH_RESULT_CLASS_TOP = 'has-searchbox-top-results';
const HAS_SEARCH_RESULT_CLASS_CART = 'has-searchbox-cart-results';

@Injectable({
    providedIn: 'root',
})
export class AimoSearchBoxComponentService extends SearchBoxComponentService {
    constructor(
        public searchService: SearchboxService,
        protected routingService: RoutingService,
        protected translationService: TranslationService,
        protected winRef: WindowRef,
        protected eventService: EventService,
        protected productSearchService: AimoProductSearchService,
    ) {
        super(searchService, routingService, translationService, winRef, eventService);
    }

    latestQuery: string;

    /**
     * Executes the search for products and suggestions,
     * unless the configuration is setup to not search for
     * products or suggestions.
     */
    search(query: string, config: AimoSearchBoxConfig): void {
        if (!query || query === '') {
            this.clearAimoResults(config.fromCartSearch);
            return;
        }

        if (config.minCharactersBeforeRequest && query.length < config.minCharactersBeforeRequest) {
            return;
        }

        if (config.displayProducts) {
            this.latestQuery = query;
            setTimeout(() => {
                if (this.latestQuery === query) {
                    this.productSearchService.search(
                        query,
                        {
                            pageSize: config.maxProducts,
                            suggestiveMode: config.suggestiveMode,
                            currentPage: config.page ? config.page : 0,
                            origin: config.origin,
                            followRedirects: true,
                            cartSearchMode: config.fromCartSearch,
                        } as AimoSearchConfig,
                        true,
                    );
                }
            }, 700);
        }

        if (config.displaySuggestions) {
            this.searchService.searchSuggestions(query, {
                pageSize: config.maxSuggestions,
                suggestiveMode: config.suggestiveMode,
                currentPage: config.page ? config.page : 0,
            } as AimoSearchConfig);
        }
    }

    getAimoResults(config: AimoSearchBoxConfig, cartSearch: boolean): Observable<AimoSearchResults> {
        return combineLatest([
            this.getProductResults(config),
            this.getAimoProductSuggestions(config),
            this.getSearchMessage(config),
        ]).pipe(
            filter(([productResults, suggestions, message]) => productResults.cartSearchMode === cartSearch),
            map(([productResults, suggestions, message]) => {
                return {
                    products: productResults ? productResults.products : undefined,
                    aimoSuggestions: productResults.categorySuggestions,
                    message,
                    pagination: productResults ? productResults.pagination : undefined,
                    keywordRedirectUrl: productResults.keywordRedirectUrl,
                    searchTerm: this.latestQuery,
                } as AimoSearchResults;
            }),
            tap((results) => {
                this.toggleBodyClass(
                    cartSearch ? HAS_SEARCH_RESULT_CLASS_CART : HAS_SEARCH_RESULT_CLASS_TOP,
                    this.hasResults(results),
                );
            }),
        );
    }

    getAimoProductSuggestions(config): Observable<AimoSuggestion[]> {
        if (!config.displaySuggestions) {
            return of([]);
        } else {
            return this.searchService.getSuggestionResults() as Observable<AimoSuggestion[]>;
        }
    }

    getProductResults(config: AimoSearchBoxConfig): Observable<AimoProductSearchPage> {
        return super.getProductResults(config) as Observable<AimoProductSearchPage>;
    }

    /**
     * Clears the searchbox results, so that old values are
     * no longer emited upon next search.
     */
    clearAimoResults(cartSearch: boolean): void {
        this.searchService.clearResults();
        this.productSearchService.closeQuickSearch();

        this.toggleBodyClass(HAS_SEARCH_RESULT_CLASS_CART, false);

        this.toggleBodyClass(HAS_SEARCH_RESULT_CLASS_TOP, false);
    }

    launchSearchPage(query: string): void {
        this.routingService.go(
            {
                cxRoute: 'search',
                params: {query},
            },
            {
                queryParams: {followRedirects: true},
            },
        );
    }
}
